// fetchUnlockedMessages.js
'use strict';
import 'isomorphic-fetch';
import parseLinkHeader from 'parse-link-header';

import getMeData from '../selector/getMeData.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import handleFetchError from '../resource/handleFetchError.js';
import { archiveType as archiveTypeConstant } from '../resource/archiveConstants.js';
import savePosts from '../action/savePosts.js';

import {
  ADD_LIST_ITEMS,
  SET_LIST_ITEMS,
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
} from '../ActionTypes.js';

/**
 * Fetch unlocked messages
 * @kind action
 * @param {number} {page} - page.
 * @param {number} {limit} - limit.
 * @param {string} {senderId} - who post the message.
 * @param {archiveTypeConstant} {type} - archive type.
 * @return {Promise} Action promise.
 */
const fetchUnlockedMessages =
  ({
    page,
    limit = 10,
    senderId = null,
    type = archiveTypeConstant.FLIX,
  } = {}) =>
  async (dispatch, getState) => {
    const listSelectPath = [
      'unlocked',
      'messages',
      type,
      senderId ? senderId : 'all',
    ];
    const networkingSelectPath = [...listSelectPath, page];
    const token = getMeData(getState(), 'token');

    const fetchOptions = {
      method: 'GET',
      headers: {
        ...getHeaders(),
        Authorization: `Bearer ${token}`,
      },
    };

    const url = getResourceUrl({ endpoint: '/unlocked' });
    url.searchParams.append('page', page);
    url.searchParams.append('limit', limit);
    if (senderId) {
      url.searchParams.append('sender', senderId);
    }
    if (type === archiveTypeConstant.FLIX) {
      url.searchParams.append('type', 'flix');
      url.searchParams.append('media', 'video');
    } else if (type === archiveTypeConstant.STORY) {
      url.searchParams.append('type', 'story');
    } else if (type === archiveTypeConstant.IMAGE) {
      url.searchParams.append('type', 'flix');
      url.searchParams.append('media', 'image');
    } else if (type === archiveTypeConstant.SHORT) {
      url.searchParams.append('type', 'flix');
      url.searchParams.append('media', 'video');
      url.searchParams.append('duration', 'SHORT');
    } else {
      return dispatch({ type: {} });
    }

    dispatch({
      type: SET_NETWORKING_FETCHING,
      payload: { selectPath: networkingSelectPath },
    });
    try {
      let response = await fetch(url.href, fetchOptions);

      if (!response.ok) {
        response = await handleFetchError({
          response,
          dispatch,
          getState,
          fetchOptions,
          fetchUrl: url,
        });
      }

      const totalCount = parseInt(response.headers.get('x-total-count'), 10);
      const links = parseLinkHeader(response.headers.get('Link'));
      const nextPage = links && links.next && parseInt(links.next.page, 10);
      const lastPage = links && links.next && parseInt(links.last.page, 10);
      const payload = (await response.json()) || [];
      dispatch(savePosts({ posts: payload }));
      const listActionType = page === 1 ? SET_LIST_ITEMS : ADD_LIST_ITEMS;
      dispatch({
        type: listActionType,
        payload: {
          selectPath: listSelectPath,
          itemIds: payload.map(item => item.id),
          totalCount,
          nextPage,
          lastPage,
        },
      });

      return dispatch({
        type: SET_NETWORKING_SUCCESS,
        payload: { selectPath: networkingSelectPath },
      });
    } catch (error) {
      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath: networkingSelectPath, error },
      });
    }
  };

export default fetchUnlockedMessages;
